import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { MessagingConstants } from '../../lib/enums';
import { IContactSupportDialogProps } from './types';

const SUPPORT_EMAIL = MessagingConstants.JOBSEEKER_SUPPORT_EMAIL;
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;

type CopyStatus = 'idle' | 'copied' | 'failed';

// The Clipboard API is blocked in iframes embedded without allow="clipboard-write" (e.g. the jobseeker site).
// Chromium exposes the policy, so check it up front instead of calling the API and triggering a violation.
const isClipboardApiAllowed = () => {
  if (!navigator.clipboard?.writeText) return false;
  const policy = (document as any).permissionsPolicy ?? (document as any).featurePolicy;
  if (policy?.allowsFeature) return policy.allowsFeature('clipboard-write');
  return true;
};

// Fallback for when the Clipboard API is unavailable or blocked.
// The textarea goes inside the dialog so the dialog's focus trap doesn't steal focus from it.
const copyWithTextarea = (text: string, container: HTMLElement) => {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  container.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    container.removeChild(textarea);
  }
};

const ContactSupportDialog = ({ open, onClose, deviceType }: IContactSupportDialogProps) => {
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  useEffect(() => {
    if (open) setCopyStatus('idle');
  }, [open]);

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget;
    const container = button.parentElement ?? document.body;
    if (isClipboardApiAllowed()) {
      try {
        await navigator.clipboard.writeText(SUPPORT_EMAIL);
        setCopyStatus('copied');
        return;
      } catch {
        // fall through to the textarea fallback
      }
    }
    setCopyStatus(copyWithTextarea(SUPPORT_EMAIL, container) ? 'copied' : 'failed');
    button.focus();
  };

  const handleEmailSupport = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (deviceType === 'web-ios') {
      // iOS specific handling: window.location.href doesn't open Mail on iOS, so use window.open.
      // If window.open is blocked, let the anchor's default mailto navigation handle the tap instead.
      const windowRef = window.open(SUPPORT_MAILTO);
      if (windowRef) {
        e.preventDefault();
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ zIndex: 10000 }}
      PaperProps={{
        sx: {
          width: 460,
          borderRadius: 3,
          p: 0,
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: '1.1rem',
          pb: 1,
          pt: 2,
          px: 3,
        }}
      >
        Contact Support
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.95rem',
            color: 'text.secondary',
            mb: 1,
          }}
        >
          Need help? Email our support team at:
        </Typography>
        <Typography
          data-testid="support-email"
          sx={{
            fontSize: '1rem',
            fontWeight: 500,
            wordBreak: 'break-all',
            userSelect: 'all',
          }}
        >
          {SUPPORT_EMAIL}
        </Typography>
        {copyStatus !== 'idle' && (
          <Typography
            role="status"
            sx={{
              fontSize: '0.85rem',
              mt: 1,
              color: copyStatus === 'copied' ? 'success.main' : 'error.main',
            }}
          >
            {copyStatus === 'copied'
              ? 'Email address copied.'
              : "Couldn't copy automatically. Please select the address above and copy it."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          px: 3,
          py: 2,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '8px',
            color: '#424242',
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleCopy}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '8px',
            px: 2.5,
            py: 0.8,
            ml: '0 !important',
          }}
        >
          Copy email
        </Button>
        {/* Plain mailto anchor so the jobseeker mobile app webviews can still intercept it */}
        <Button
          component="a"
          href={SUPPORT_MAILTO}
          target="_top"
          onClick={handleEmailSupport}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            px: 2.5,
            py: 0.8,
            ml: '0 !important',
          }}
        >
          Email Support
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactSupportDialog;
